import { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DiscrepancyReason, DISCREPANCY_REASON_LABELS } from '@/types/trip.types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trackingNumber: string;
  submitting?: boolean;
  onSubmit: (reason: DiscrepancyReason, notes: string) => void | Promise<void>;
}

export function DiscrepancyDialog({ open, onOpenChange, trackingNumber, submitting, onSubmit }: Props) {
  const [reason, setReason] = useState<DiscrepancyReason | ''>('');
  const [notes, setNotes] = useState('');

  const reset = () => {
    setReason('');
    setNotes('');
  };

  const handleSubmit = async () => {
    if (!reason) return;
    await onSubmit(reason, notes.trim());
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>تسجيل تباين</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">رقم التتبع</p>
            <p className="font-mono">{trackingNumber}</p>
          </div>
          <div className="space-y-2">
            <Label>السبب *</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as DiscrepancyReason)} disabled={submitting}>
              <SelectTrigger>
                <SelectValue placeholder="اختر السبب" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(DISCREPANCY_REASON_LABELS) as DiscrepancyReason[]).map((r) => (
                  <SelectItem key={r} value={r}>
                    {DISCREPANCY_REASON_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>ملاحظات (اختياري)</Label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="أي تفاصيل إضافية عن التباين..."
              disabled={submitting}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            إلغاء
          </Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={submitting || !reason}>
            {submitting ? 'جارٍ الحفظ...' : 'تأكيد التباين'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
